import { useMemo, useEffect, useState, useRef } from 'react';
import { Divider, Box, Typography } from '@mui/material';
import { listCard } from '~/components/Global/Card/Card.style';
import {
  BaseButton,
  BASE_BUTTON_VARIANT,
  BASE_BUTTON_COLOR,
} from '~/components/Global/Button/BaseButton';
import { TDownloadableResource } from '~/types/common.type';
import {
  openMode,
  useDownload,
  isWebBatchDownloadOverSizeLimit,
} from '~/utils/hook/useDownload';
import { AlertDialog } from '~/components/Global/Dialog/AlertDialog';
import { FileStatus } from '~/store/resource/resource.type';
import {
  getFileUpdateStatus,
  getOverallFileStatus,
} from '~/utils/helper/function';
import { OverallFileStatus, UpdateStatus } from '~/types/enum';
import { set, get } from 'lodash';
import {
  canExtract,
  getExtractPayload,
} from '~/utils/hook/useAutoExtractEffect';
import { sendChannel } from '~/utils/constants';
import { useAppDispatch } from '~/store';
import {
  addTotalDownloadingItem,
  removeTotalDownloadingItem,
} from '~/store/resource';
import { useSelector } from 'react-redux';
import { getterHasTotalDownloading } from '~/store/resource/resourceGetter';
import { RootState } from '~/store';
import { setShowLoginDialog } from '~/store/user';
import { getterIsValidLogin } from '~/store/user/userGetter';
import { isWeb } from '~/utils/helper/device';
import { sortObjectByKeyNumber } from '~/utils/helper/function';
import { downloadAllStartEvent } from '~/tracking';
import { useCommon } from '~/utils/hook/useCommon';

type TListCardItem = {
  label: string;
  content: TDownloadableResource[];
  openSingleDownload: () => void;
  labelSeries: string[];
};

export const ListCardItem = ({
  label,
  content,
  openSingleDownload,
  labelSeries,
}: TListCardItem) => {
  const isTotalDownloading = useSelector((state: RootState) =>
    getterHasTotalDownloading(state)(labelSeries),
  );
  const { openExternal } = useCommon();
  const {
    multipleFileDownload,
    multipleFileUpdate,
    extract,
    multipleFilePause,
    multipleFileResume,
  } = useDownload();
  const dispatch = useAppDispatch();
  const [isS3, setIsS3] = useState(false);
  const [showSizeLimitAlert, setShowSizeLimitAlert] = useState(false);
  // 每個 key 上一次觀察到的下載狀態,用來偵測「轉入 completed」的轉場
  const prevStatusRef = useRef(new Map<string, FileStatus>());
  const overview = useMemo(() => {
    const map = {
      [UpdateStatus.old]: [] as TDownloadableResource[],
      [UpdateStatus.latest]: [] as TDownloadableResource[],
      ratio: 0,
      hasCanceled: false,
    };

    // 群組進度改為逐檔下載進度的平均，而非「完成檔案數比例」——
    // 後者要等第一個檔案整個跑完才會離開 0%，批次下載會長時間顯示 0%。
    // completed / installing 代表位元組已全數收到（收尾與安裝進度另計），
    // 下載進度視同 100。
    let downloadRatioSum = 0;

    content.forEach(item => {
      const { progress, resource } = item;
      const { status, downloadedAt, progressRatio } = progress;
      const { fileUploadTime, dataSource } = resource.file;
      if (dataSource === 's3Iso') {
        setIsS3(true);
      }
      if (
        status === FileStatus.finish ||
        status === FileStatus.completed ||
        status === FileStatus.installing
      ) {
        downloadRatioSum += 100;
      } else if (
        status === FileStatus.progressing ||
        status === FileStatus.paused
      ) {
        downloadRatioSum += progressRatio;
      }
      if (progress.status === FileStatus.cancel) {
        set(map, ['hasCanceled'], true);
      }

      const updateStatus = getFileUpdateStatus({
        downloadedAt: downloadedAt,
        uploadedAt: fileUploadTime,
      });

      if (updateStatus === UpdateStatus.old) {
        set(
          map,
          [UpdateStatus.old],
          [...get(map, [UpdateStatus.old], []), item],
        );
      }

      if (
        updateStatus === UpdateStatus.latest &&
        status === FileStatus.finish
      ) {
        set(
          map,
          [UpdateStatus.latest],
          [...get(map, [UpdateStatus.latest], []), item],
        );
      }
    });

    // 加總後才除，避免逐項累加 100/N 的浮點誤差讓全數完成時顯示 99%
    map.ratio = content.length > 0 ? downloadRatioSum / content.length : 0;

    return map;
  }, [content]);

  const isInternetResource = content.some(
    item =>
      item.resource.file.dataSource === 'internet' ||
      item.resource.file.resource2 === '網頁PPT',
  );
  const internetUrl = isInternetResource ? content[0]?.resource?.file?.url : '';

  const overallStatus = getOverallFileStatus(content);

  const ownInfo = useSelector((state: RootState) => state.user.ownInfo);
  const isValidLogin = useSelector(getterIsValidLogin);

  // 不可用 useCallback 包:deps 只看得到 content reference,
  // 登入狀態(isValidLogin)之後才回寫時會被舊閉包鎖住,導致已登入仍跳「尚未登入」
  const handleAction = (action: keyof typeof sendChannel) => {
    if (!ownInfo || !isValidLogin) {
      dispatch(setShowLoginDialog(true));
      return;
    }

    if (action === sendChannel.multipleFileDownload) {
      // 網頁版 zip 打包超過 4GiB 會產生損毀檔，超限時擋下並提示逐檔下載
      if (isWebBatchDownloadOverSizeLimit(content)) {
        setShowSizeLimitAlert(true);
        return;
      }
      const firstResource = content[0]?.resource;

      if (firstResource) {
        downloadAllStartEvent({
          edu: firstResource.eduCodes[0],
          subject: firstResource.subjects[0],
          category: firstResource.resources1[0],
          resources_label: firstResource.resources2[0],
          resources_sub_label: firstResource.resources3[0],
        });
      }
      dispatch(addTotalDownloadingItem(labelSeries));
      multipleFileDownload({
        list: content,
        mode: openMode.link,
      });
    }

    if (action === sendChannel.multipleFileUpdate) {
      multipleFileUpdate(overview[UpdateStatus.old]);
    }

    if (action === sendChannel.multipleFileResume) {
      const keys: string[] = [];
      content.forEach(item => {
        const { resource } = item;
        const { dataSource } = resource.file;
        if (dataSource === 's3Iso') {
          setIsS3(true);
        }
        if (item.progress.key) {
          keys.push(item.progress.key);
        }
      });

      multipleFileResume(keys);
    }
    if (action === sendChannel.multipleFilePause) {
      const keys: string[] = [];
      content.forEach(item => {
        const { resource } = item;
        const { dataSource } = resource.file;
        if (dataSource === 's3Iso') {
          setIsS3(true);
        }
        if (item.progress.key) {
          keys.push(item.progress.key);
        }
      });

      multipleFilePause(keys);
    }
  };

  // 只在狀態「轉入 completed」的那一次發 extract:
  // content 內任一項目的進度更新都會觸發本 effect,
  // 若無條件對所有 completed 項目重發,批次下載時會形成 extract 風暴
  useEffect(() => {
    content.forEach(item => {
      const { status, key } = item.progress;
      if (!key) return;
      const prevStatus = prevStatusRef.current.get(key);
      if (
        status === FileStatus.completed &&
        prevStatus !== FileStatus.completed
      ) {
        const extractPayload = getExtractPayload(item);
        if (canExtract(extractPayload)) {
          extract(extractPayload);
        }
      }
      prevStatusRef.current.set(key, status);
    });
  }, [content]);

  useEffect(() => {
    if (content.length === overview[UpdateStatus.latest].length) {
      dispatch(removeTotalDownloadingItem(labelSeries));
    }
  }, [content, overview[UpdateStatus.latest]]);

  useEffect(() => {
    if (overview.hasCanceled) {
      dispatch(removeTotalDownloadingItem(labelSeries));
    }
  }, [overview.hasCanceled]);

  return (
    <Box key={label} sx={{ position: 'relative' }}>
      {isTotalDownloading && !isWeb && (
        <listCard.downloadingContainer>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <listCard.ProgressBar
              status={FileStatus.installing}
              variant="determinate"
              value={overview.ratio}
            />
            <Typography
              fontSize={18}
              lineHeight={1.2}
              color="white"
              component="span"
            >
              {Math.floor(overview.ratio)} %
            </Typography>
          </Box>
          <listCard.Action>
            {overallStatus === OverallFileStatus.paused && (
              <BaseButton
                variant={BASE_BUTTON_VARIANT.FILLED}
                color={BASE_BUTTON_COLOR.CONTINUE}
                text="繼續"
                onClick={() => handleAction(sendChannel.multipleFileResume)}
                width="88px"
                style={{ fontSize: '18px', padding: '6px 14px' }}
              />
            )}
            {overallStatus === OverallFileStatus.progressing && (
              <BaseButton
                // 這個 Button Hover 時的 style 需要再確認
                variant={BASE_BUTTON_VARIANT.OUTLINED}
                color={BASE_BUTTON_COLOR.DOWNLOAD}
                text="暫停"
                onClick={() => handleAction(sendChannel.multipleFilePause)}
                width="88px"
                style={{ fontSize: '18px' }}
              />
            )}
          </listCard.Action>
        </listCard.downloadingContainer>
      )}
      <listCard.ListItem>
        <listCard.ListItemLabel>{label}</listCard.ListItemLabel>
        <listCard.Action>
          {isInternetResource ? (
            // 網路資源：只顯示連結按鈕
            <BaseButton
              variant={BASE_BUTTON_VARIANT.GHOST}
              color={BASE_BUTTON_COLOR.SUCCESS}
              text="前往"
              onClick={() => {
                if (internetUrl) {
                  openExternal(internetUrl);
                }
              }}
              width="88px"
              style={{ fontSize: '18px' }}
            />
          ) : (
            // 原本的下載邏輯保持不變
            <>
              <BaseButton
                variant={BASE_BUTTON_VARIANT.GHOST}
                color={BASE_BUTTON_COLOR.PRIMARY}
                text="單檔下載"
                onClick={openSingleDownload}
                width="88px"
                style={{ fontSize: '18px' }}
              />
              {!isS3 && (
                <BaseButton
                  variant={BASE_BUTTON_VARIANT.GHOST}
                  color={BASE_BUTTON_COLOR.PRIMARY}
                  text={'全部下載'}
                  onClick={() => handleAction(sendChannel.multipleFileDownload)}
                  width="88px"
                  style={{ fontSize: '18px' }}
                />
              )}

              {overview[UpdateStatus.old].length > 0 && (
                <BaseButton
                  variant={BASE_BUTTON_VARIANT.GHOST}
                  color={BASE_BUTTON_COLOR.SECONDARY}
                  text="資源更新"
                  onClick={() => handleAction(sendChannel.multipleFileUpdate)}
                  width="88px"
                  style={{ fontSize: '18px' }}
                />
              )}
            </>
          )}
        </listCard.Action>
      </listCard.ListItem>
      <AlertDialog
        open={showSizeLimitAlert}
        title="批次下載提醒"
        onClose={() => setShowSizeLimitAlert(false)}
      >
        所選檔案總計超過 3.8GB，超出批次打包上限，
        <br />
        可能導致下載檔案損毀。
        <br />
        請改用單一檔案方式逐一下載。
      </AlertDialog>
      <Divider />
    </Box>
  );
};

type TProp = {
  handleSingleDownload: (subLabel: string) => void;
  list: {
    [resource3: string]: TDownloadableResource[];
  };
  label: string;
};

export const ListCard = ({ handleSingleDownload, list, label }: TProp) => {
  return (
    <listCard.Container>
      <listCard.Head title={label} />
      <listCard.Content>
        {Object.entries(sortObjectByKeyNumber(list)).map(
          ([sublabel, content]) => (
            <ListCardItem
              key={sublabel}
              labelSeries={[label, sublabel]}
              label={sublabel}
              content={content}
              openSingleDownload={() => {
                handleSingleDownload(sublabel);
              }}
            />
          ),
        )}
      </listCard.Content>
    </listCard.Container>
  );
};
